/**
 * Shopify Wishlist Functionality
 * Uses localStorage to store wishlist data
 * 
 * Products are identified by their ID (not handle) to support multi-language stores.
 * This ensures the same product is not added multiple times across different locales.
 * 
 * Storage format: JSON array of objects [{id, handle}, ...]
 */

const LOCAL_STORAGE_WISHLIST_KEY = "shopify-wishlist";
const BUTTON_ACTIVE_CLASS = "is-active";
const GRID_LOADED_CLASS = "is-loaded";

const selectors = {
  button: ".js-wishlist-button",
  grid: ".js-wishlist-grid",
  productCard: ".js-wishlist-product-card",
  counter: ".js-wishlist-counter",
  emptyMessage: ".js-wishlist-empty",
};

/**
 * Normalizes Shopify product ID to numeric string
 * Handles both numeric IDs and GID format (gid://shopify/Product/123)
 * @param {string|number} id - Product ID
 * @returns {string} Normalized numeric ID
 */
const normalizeProductId = (id) => {
  if (!id) return '';
  
  const idString = String(id);
  
  // Check if it's a GID format: gid://shopify/Product/123456
  if (idString.includes('gid://shopify/Product/')) {
    return idString.split('/').pop();
  }
  
  // Already numeric ID
  return idString;
};

/**
 * Migrates old wishlist format to new JSON format
 * Supports both handle-based and ID-based old formats
 */
const migrateWishlist = async () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY);
  
  if (!stored) {
    return;
  }
  
  // Check if already in new format (JSON array of objects)
  try {
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].id && parsed[0].handle) {
      // Already in new format
      return;
    }
  } catch (e) {
    // Not JSON, needs migration
  }
  
  const migratedItems = [];
  const oldItems = stored.includes(',') ? stored.split(',') : [stored];
  const processedIds = new Set();
  
  const localePrefix = getCurrentLocalePrefix();
  
  for (const item of oldItems) {
    if (!item) continue;
    
    try {
      let productData;
      
      // Check if it's an ID (numeric) or handle (string)
      if (!isNaN(item)) {
        // It's an ID - we cannot reliably fetch by ID, skip this item
        // User will need to re-add it to wishlist
        console.warn(`[Shopify Wishlist] Cannot migrate numeric ID: ${item}. Product needs to be re-added.`);
        continue;
      } else {
        // It's a handle - fetch product data to get ID
        const response = await fetch(`${localePrefix}/products/${item}.js`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        productData = await response.json();
      }
      
      if (productData && productData.id && productData.handle) {
        const productId = normalizeProductId(productData.id);
        
        // Avoid duplicates (same product in different languages)
        if (!processedIds.has(productId)) {
          migratedItems.push({
            id: productId,
            handle: productData.handle
          });
          processedIds.add(productId);
        }
      }
    } catch (err) {
      console.error(`[Shopify Wishlist] Failed to migrate item: ${item}`, err);
    }
  }
  
  // Save migrated wishlist
  if (migratedItems.length > 0) {
    setWishlist(migratedItems);
  } else {
    // Clear invalid data
    localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
  }
};

/**
 * Initialize on page load
 */
document.addEventListener("DOMContentLoaded", async () => {
  await migrateWishlist();
  initClickDelegation();
  observeDynamicButtons();
  initButtons();
  initGrid();
  updateCounters();
});

/**
 * Listen for wishlist updates
 */
document.addEventListener("shopify-wishlist:updated", (event) => {
  initGrid();
  updateCounters();
  syncButtons();
});

/**
 * Gets current locale prefix from Shopify
 * @returns {string} Locale prefix (e.g., '/en', '/ru', or empty string for default)
 */
const getCurrentLocalePrefix = () => {
  // Use Shopify's localization data from themeVariables
  // locale.rootUrl is already formatted: '' for default locale, '/en' for other locales
  if (window.themeVariables?.locale?.rootUrl !== undefined) {
    return window.themeVariables.locale.rootUrl;
  }
  
  // Fallback to routes.rootUrlWithoutSlash if locale is not available
  if (window.themeVariables?.routes?.rootUrlWithoutSlash) {
    return window.themeVariables.routes.rootUrlWithoutSlash;
  }
  
  // Last resort: parse from URL (should rarely happen)
  console.warn('[Shopify Wishlist] Could not get locale from themeVariables, parsing URL');
  const path = window.location.pathname;
  const localeMatch = path.match(/^\/([a-z]{2})(?:\/|$)/);
  
  return localeMatch ? `/${localeMatch[1]}` : '';
};

/**
 * Fetches HTML product card via fetch API
 * Uses the stored handle and tries with current locale first, then without locale prefix
 * @param {Object} item - Wishlist item with {id, handle}
 * @returns {Promise<string>} HTML product card
 */
const fetchProductCardHTML = async (item) => {
  const localePrefix = getCurrentLocalePrefix();
  
  // Try with current locale first
  let productTileTemplateUrl = `${localePrefix}/products/${item.handle}?view=wishlist`;
  
  try {
    const res = await fetch(productTileTemplateUrl);
    
    // If 404 and we're on a non-default locale, try without locale prefix
    // This helps when handle was saved in a different language
    if (!res.ok && res.status === 404 && localePrefix) {
      productTileTemplateUrl = `/products/${item.handle}?view=wishlist`;
      const fallbackRes = await fetch(productTileTemplateUrl);
      
      if (!fallbackRes.ok) {
        throw new Error(`HTTP ${fallbackRes.status}: ${fallbackRes.statusText}`);
      }
      
      return parseProductCard(await fallbackRes.text(), item.handle);
    }
    
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    
    return parseProductCard(await res.text(), item.handle);
  } catch (err) {
    console.error(
      `[Shopify Wishlist] Failed to load content for product: ${item.handle} (ID: ${item.id})`,
      err
    );
    return '';
  }
};

/**
 * Parses product card HTML from response
 * @param {string} html - HTML response
 * @param {string} handle - Product handle for logging
 * @returns {string} Product card HTML or empty string
 */
const parseProductCard = (html, handle) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const productCard = doc.querySelector(selectors.productCard);
  
  if (!productCard) {
    console.warn(`[Shopify Wishlist] Product card not found for: ${handle}`);
    return '';
  }
  
  return productCard.outerHTML;
};

/**
 * Sets up wishlist grid on wishlist page
 * @param {HTMLElement} grid - Grid element
 */
const setupGrid = async (grid) => {
  const wishlist = getWishlist();
  const requests = wishlist.map(fetchProductCardHTML);
  const wishlistProductCards = (await Promise.all(requests)).join("");
  
  grid.innerHTML = wishlistProductCards;
  grid.classList.add(GRID_LOADED_CLASS);
  grid.classList.remove("is-loading");
  
  initButtons();
  
  // Initialize additional functions if available
  if (typeof initButtonsCompare !== "undefined") {
    initButtonsCompare();
  }
  
  if (typeof wpbingo !== "undefined") {
    if (wpbingo.countdown) {
      wpbingo.countdown();
    }
    
    if (wpbingo.click_atribute_image) {
      wpbingo.click_atribute_image();
    }
  }
  
  // Multi-currency support
  const currencyElement = document.querySelector(".bwp_currency");
  
  if (currencyElement && wishlist.length > 0) {
    if (typeof Currency !== "undefined" && Currency.Currency_customer) {
      Currency.Currency_customer(true);
    }
  }
  
  // Ajax cart initialization
  if (typeof ajaxCart !== "undefined" && ajaxCart.init) {
    ajaxCart.init();
  }
  
  // Dispatch custom event
  const event = new CustomEvent("shopify-wishlist:init-product-grid", {
    detail: { wishlist },
  });
  document.dispatchEvent(event);
  
  // Show/hide empty wishlist message
  const emptyElement = document.querySelector(selectors.emptyMessage);
  
  if (emptyElement) {
    if (wishlist.length > 0) {
      emptyElement.classList.add("hidden");
    } else {
      emptyElement.classList.remove("hidden");
    }
  }
};

let isClickDelegated = false;
let dynamicButtonsObserver = null;

/**
 * Registers a single delegated click handler on the document.
 * Using delegation ensures buttons added dynamically (AJAX pagination,
 * filters, quick-view) work without rebinding listeners.
 */
const initClickDelegation = () => {
  if (isClickDelegated) {
    return;
  }
  
  isClickDelegated = true;
  
  document.addEventListener("click", (event) => {
    const button = event.target.closest(selectors.button);
    
    if (!button) {
      return;
    }
    
    const productId = normalizeProductId(button.dataset.productId);
    const productHandle = button.dataset.productHandle;
    
    if (!productId || !productHandle) {
      console.warn('[Shopify Wishlist] Button missing data:', button);
      return;
    }
    
    handleButtonClick(button, productId, productHandle);
  });
};

/**
 * Watches the DOM for dynamically inserted wishlist buttons and syncs
 * their active state. Required because the collection AJAX pagination
 * replaces the product grid innerHTML without dispatching an event.
 */
const observeDynamicButtons = () => {
  if (dynamicButtonsObserver || !("MutationObserver" in window)) {
    return;
  }
  
  dynamicButtonsObserver = new MutationObserver((mutations) => {
    const hasNewButton = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.(selectors.button) || node.querySelector?.(selectors.button))
      )
    );
    
    if (hasNewButton) {
      syncButtons();
    }
  });
  
  dynamicButtonsObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
};

/**
 * Syncs active state and tooltip of all buttons with the stored wishlist.
 * Skips buttons mid-animation to avoid overriding the optimistic toggle.
 */
const syncButtons = () => {
  const buttons = document.querySelectorAll(selectors.button);
  
  for (const button of buttons) {
    if (button.classList.contains("is-loading")) {
      continue;
    }
    
    const productId = normalizeProductId(button.dataset.productId);
    
    if (!productId) {
      continue;
    }
    
    button.classList.toggle(BUTTON_ACTIVE_CLASS, wishlistContains(productId));
    updateButtonTooltip(button);
  }
};

/**
 * Handles wishlist button click
 * @param {HTMLElement} button - Button element
 * @param {string} productId - Product ID
 * @param {string} productHandle - Product handle
 */
const handleButtonClick = (button, productId, productHandle) => {
  updateWishlist(productId, productHandle);
  
  const isActive = wishlistContains(productId);
  button.classList.add("is-loading");
  
  setTimeout(() => {
    button.classList.remove("is-loading");
    button.classList.toggle(BUTTON_ACTIVE_CLASS, isActive);
    
    updateButtonTooltip(button);
  }, 1000);
};

/**
 * Updates button tooltip based on state
 * @param {HTMLElement} button - Button element
 */
const updateButtonTooltip = (button) => {
  const parent = button.closest(".button-wishlist");
  
  if (!parent) {
    return;
  }
  
  const isActive = button.classList.contains(BUTTON_ACTIVE_CLASS);
  const strings = window.themeVariables?.strings || window.strings || {};
  const tooltipText = isActive 
    ? strings.remove_wishlist || 'Remove from wishlist'
    : strings.wishlist || 'Add to wishlist';
  
  parent.setAttribute("data-title", tooltipText);
};

/**
 * Initializes wishlist grid
 */
const initGrid = () => {
  const grid = document.querySelector(selectors.grid);
  
  if (!grid) {
    return;
  }
  
  setupGrid(grid);
};

/**
 * Initializes wishlist buttons
 * @param {string} containerSelector - Container selector (optional)
 */
const initButtons = (containerSelector) => {
  let buttons;
  
  if (containerSelector) {
    const container = document.querySelector(containerSelector);
    buttons = container ? container.querySelectorAll(selectors.button) : [];
  } else {
    buttons = document.querySelectorAll(selectors.button);
  }
  
  if (!buttons.length) {
    return;
  }
  
  // Clicks are handled via delegation (initClickDelegation), so here we
  // only sync visual state and tooltips for the current buttons.
  for (const button of buttons) {
    if (button.classList.contains("is-loading")) {
      continue;
    }
    
    const productId = normalizeProductId(button.dataset.productId);
    
    if (productId) {
      button.classList.toggle(BUTTON_ACTIVE_CLASS, wishlistContains(productId));
    }
  }
  
  updateButtonTooltips(buttons);
  
  // Dispatch custom event
  const event = new CustomEvent("shopify-wishlist:init-buttons", {
    detail: { wishlist: getWishlist() },
  });
  document.dispatchEvent(event);
};

/**
 * Updates tooltips for all buttons
 * @param {NodeList} buttons - List of button elements
 */
const updateButtonTooltips = (buttons) => {
  for (const button of buttons) {
    updateButtonTooltip(button);
  }
};

/**
 * Gets wishlist from localStorage
 * @returns {Array<Object>} Array of wishlist items [{id, handle}, ...]
 */
const getWishlist = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY);
  
  if (!stored) {
    return [];
  }
  
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('[Shopify Wishlist] Failed to parse wishlist data', e);
    return [];
  }
};

/**
 * Saves wishlist to localStorage
 * @param {Array<Object>} items - Array of wishlist items [{id, handle}, ...]
 */
const setWishlist = (items) => {
  if (items.length) {
    localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, JSON.stringify(items));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
  }
  
  // Dispatch custom event
  const event = new CustomEvent("shopify-wishlist:updated", {
    detail: { wishlist: items },
  });
  document.dispatchEvent(event);
};

/**
 * Adds or removes product from wishlist
 * @param {string} productId - Product ID (already normalized)
 * @param {string} productHandle - Product handle
 */
const updateWishlist = (productId, productHandle) => {
  const wishlist = getWishlist();
  const normalizedId = normalizeProductId(productId);
  const indexInWishlist = wishlist.findIndex(item => normalizeProductId(item.id) === normalizedId);
  
  if (indexInWishlist === -1) {
    wishlist.push({ id: normalizedId, handle: productHandle });
  } else {
    wishlist.splice(indexInWishlist, 1);
  }
  
  setWishlist(wishlist);
};

/**
 * Updates all wishlist counters
 */
const updateCounters = () => {
  const wishlist = getWishlist();
  const countElements = document.querySelectorAll(selectors.counter);
  
  for (const element of countElements) {
    element.textContent = wishlist.length;
  }
};

/**
 * Checks if product is in wishlist
 * @param {string} productId - Product ID
 * @returns {boolean}
 */
const wishlistContains = (productId) => {
  const normalizedId = normalizeProductId(productId);
  return getWishlist().some(item => normalizeProductId(item.id) === normalizedId);
};

/**
 * Clears wishlist
 * @returns {string} Empty string
 */
const resetWishlist = () => {
  return setWishlist([]);
};

// Export for use in other modules if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    getWishlist,
    setWishlist,
    updateWishlist,
    wishlistContains,
    resetWishlist,
    initButtons,
    initGrid,
    updateCounters,
  };
}
